import apiService from './api';
import {
  Page,
  PageWithSections,
  PageSection,
  SectionContent,
  SectionContentByLanguage,
  CreateSectionRequest,
  UpdateSectionRequest,
  ReorderSectionsRequest,
  CreateContentRequest,
  UpdateContentRequest,
  DeleteContentRequest,
  ApiResponse,
  PageName,
  Language
} from '../types/pageBuilder';

const BASE_URL = '/admin/pages';

/**
 * Page Builder Service
 * Handles all API calls for the page builder functionality
 */
const pageBuilderService = {
  /**
   * Get list of all editable pages
   */
  async getPages(): Promise<Page[]> {
    const response = await apiService.get<{ pages: Page[] }>(BASE_URL);
    return response.pages || [];
  },

  /**
   * Get a specific page with all sections and content
   */
  async getPage(
    pageName: PageName,
    language: Language = 'en'
  ): Promise<PageWithSections> {
    const response = await apiService.get<{ page: PageWithSections }>(
      `${BASE_URL}?page_name=${pageName}&language=${language}`
    );
    if (!response.page) {
      throw new Error('Page not found');
    }
    return response.page;
  },

  /**
   * Get all sections for a specific page
   */
  async getSections(pageName: PageName): Promise<PageSection[]> {
    const response = await apiService.get<{ sections: PageSection[] }>(
      `${BASE_URL}/sections?page=${pageName}`
    );
    return response.sections || [];
  },

  /**
   * Create a new section
   */
  async createSection(data: CreateSectionRequest): Promise<PageSection> {
    const response = await apiService.post<{ section: PageSection }>(
      `${BASE_URL}/sections`,
      data
    );
    if (!response.section) {
      throw new Error('Failed to create section');
    }
    return response.section;
  },

  /**
   * Update a section (visibility, order, config)
   */
  async updateSection(
    sectionId: number,
    data: UpdateSectionRequest
  ): Promise<PageSection> {
    const response = await apiService.put<{ section: PageSection }>(
      `${BASE_URL}/sections/${sectionId}`,
      data
    );
    if (!response.section) {
      throw new Error('Failed to update section');
    }
    return response.section;
  },

  /**
   * Delete a section
   */
  async deleteSection(sectionId: number): Promise<void> {
    await apiService.delete(`${BASE_URL}/sections/${sectionId}`);
  },

  /**
   * Reorder multiple sections (for drag-and-drop)
   */
  async reorderSections(data: ReorderSectionsRequest): Promise<void> {
    await apiService.post(`${BASE_URL}/sections/reorder`, data);
  },

  /**
   * Get content for a specific section
   */
  async getSectionContent(
    sectionId: number,
    language?: Language
  ): Promise<SectionContent | SectionContentByLanguage> {
    const url = language
      ? `${BASE_URL}/content?section_id=${sectionId}&lang=${language}`
      : `${BASE_URL}/content?section_id=${sectionId}`;

    const response = await apiService.get<{ content: SectionContent | SectionContentByLanguage }>(url);
    return response.content || {};
  },

  /**
   * Create new content field for a section
   */
  async createContent(data: CreateContentRequest): Promise<void> {
    await apiService.post(`${BASE_URL}/content`, data);
  },

  /**
   * Update section content (single field or bulk)
   */
  async updateContent(data: UpdateContentRequest): Promise<void> {
    await apiService.put(`${BASE_URL}/content`, data);
  },

  /**
   * Delete a content field
   */
  async deleteContent(data: DeleteContentRequest): Promise<void> {
    const { section_id, language, field_key } = data;
    await apiService.delete(
      `${BASE_URL}/content?section_id=${section_id}&language=${language}&field_key=${field_key}`
    );
  },

  /**
   * Toggle section visibility
   */
  async toggleSectionVisibility(
    sectionId: number,
    isVisible: boolean
  ): Promise<PageSection> {
    return this.updateSection(sectionId, {
      is_visible: isVisible ? 1 : 0
    });
  },

  /**
   * Update section order
   */
  async updateSectionOrder(
    sectionId: number,
    displayOrder: number
  ): Promise<PageSection> {
    return this.updateSection(sectionId, {
      display_order: displayOrder
    });
  },

  /**
   * Bulk update content fields for a section
   */
  async bulkUpdateContent(
    sectionId: number,
    language: Language,
    fields: { [key: string]: string | any[] }
  ): Promise<void> {
    await this.updateContent({
      section_id: sectionId,
      language,
      fields
    });
  },

  /**
   * Update single content field
   */
  async updateSingleField(
    sectionId: number,
    language: Language,
    fieldKey: string,
    fieldValue: string | any[]
  ): Promise<void> {
    await this.updateContent({
      section_id: sectionId,
      language,
      field_key: fieldKey,
      field_value: fieldValue
    });
  }
};

export default pageBuilderService;
